import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ShoppingBag, Check, FileText } from "lucide-react";
import api from "../lib/api";
import { fmtPrice } from "../components/Reveal";
import { useStore } from "../context/StoreContext";
import { useLang } from "../context/LanguageContext";
import { resolveAsset } from "../hooks/useSiteContent";
import QuoteModal from "../components/QuoteModal";

const HIGH_VALUE = 50000;

export default function ProductDetail() {
  const { slug } = useParams();
  const { t } = useLang();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const { addToCart } = useStore();

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`).then(({ data }) => setProduct(data)).catch(() => setProduct(false));
  }, [slug]);

  if (product === null) return <div className="pt-40 text-center font-mono text-ink/40">{t("common.loading")}</div>;
  if (product === false) return <div className="pt-40 text-center font-mono text-ink/40">{t("common.notFound")}</div>;

  const gallery = [product.image, ...(product.gallery || [])].filter((v, i, a) => v && a.indexOf(v) === i);
  const isHighValue = product.category === "physical" && product.price >= HIGH_VALUE;

  return (
    <div className="pt-28 md:pt-32" data-testid="product-detail">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <Link to="/catalogo" className="inline-flex items-center gap-2 text-sm text-ink/50 hover:text-gold transition-colors mb-8"><ArrowLeft className="w-4 h-4" /> {t("pdp.back")}</Link>

        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 lg:gap-16 pb-28">
          <div className="space-y-4">
            {gallery.map((img, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1, duration: 0.7 }}
                className="relative overflow-hidden bg-surface aspect-[4/3]">
                <img src={resolveAsset(img)} alt={product.name} className="w-full h-full object-cover" />
              </motion.div>
            ))}
          </div>

          <div className="lg:sticky lg:top-28 lg:self-start">
            <span className="overline text-gold">{t(`cat.${product.category}`)}</span>
            <h1 className="font-display font-extrabold tracking-tighter text-[clamp(2rem,4vw,3.4rem)] leading-[0.95] mt-4">{product.name}</h1>
            <p className="font-mono text-gold text-sm mt-3">{product.tagline}</p>

            <p className="font-display font-extrabold text-4xl mt-8">
              {fmtPrice(product.price, product.currency)}
              {product.unit && <span className="text-ink/40 text-lg font-sans font-normal"> / {product.unit}</span>}
            </p>

            <p className="text-ink/65 leading-relaxed mt-6">{product.description}</p>

            {product.specs?.length > 0 && (
              <div className="mt-8 border-t border-ink/10">
                {product.specs.map((s, i) => (
                  <div key={i} className="flex justify-between py-3 border-b border-ink/10 text-sm">
                    <span className="text-ink/50">{s.label}</span>
                    <span className="font-medium font-mono">{s.value}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-3 mt-10">
              <div className="flex items-center border border-ink/15">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-4 hover:text-gold" data-testid="pdp-dec">−</button>
                <span className="px-4 font-mono" data-testid="pdp-qty">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="px-4 py-4 hover:text-gold" data-testid="pdp-inc">+</button>
              </div>
              <button data-testid="pdp-add-cart"
                onClick={() => { addToCart(product, qty); setAdded(true); setTimeout(() => setAdded(false), 1800); }}
                className="flex-1 bg-blue text-[color:var(--bg)] flex items-center justify-center gap-2 hover:bg-gold hover:text-inverse transition-colors duration-300 font-medium">
                {added ? <><Check className="w-4 h-4" /> {t("pdp.added")}</> : <><ShoppingBag className="w-4 h-4" /> {t("pdp.add")}</>}
              </button>
            </div>

            {isHighValue && (
              <div className="mt-4">
                <button onClick={() => setQuoteOpen(true)} data-testid="request-quote-btn"
                  className="w-full border border-gold text-gold py-4 flex items-center justify-center gap-2 hover:bg-gold hover:text-inverse transition-colors duration-300 font-medium">
                  <FileText className="w-4 h-4" /> {t("pdp.quote")}
                </button>
                <p className="text-xs text-ink/40 mt-2 font-mono text-center">{t("pdp.quoteNote")}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {quoteOpen && <QuoteModal product={product} onClose={() => setQuoteOpen(false)} />}
      </AnimatePresence>
    </div>
  );
}
